import { createSelector } from 'reselect'
import { sum } from 'lib/array/arrayUtils'
import { getAvailableAccommodationBenefits } from 'luxPlus/selectors/benefits/accommodation'
import { getAvailableCruiseBenefits } from 'luxPlus/selectors/benefits/cruise'
import { checkoutAccommodationOfferView } from 'checkout/selectors/view/accommodation'
import { getVillaViews } from 'checkout/selectors/view/villa'
import { getShouldShowLuxPlusUpsellForTour } from 'checkout/selectors/tourV2Selectors'
import { getAvailableInsuranceBenefits } from 'luxPlus/selectors/benefits/insurance'
import { getAvailableBookingProtectionBenefits } from 'luxPlus/selectors/benefits/bookingProtection'
import { getAvailableExperienceBenefits } from 'luxPlus/selectors/benefits/experience'
import { getFlightTotals } from 'checkout/selectors/payment/flights'
import { getGiftCardItems } from 'checkout/selectors/view/giftCard'

/**
 * Total savings from LuxPlus-caused vouchers applied in the current checkout.
 */
export const getLuxPlusLoyaltyVoucherSavings = createSelector(
  (state: App.State) => state.checkout.promo,
  (state: App.State) => state.marketing.vouchers,
  (promo, vouchers): number => {
    if (!promo?.items?.length || !vouchers.length) return 0

    const loyaltyVoucherIds = new Set(
      vouchers
        .filter(v => v.voucherToggles.includes('is-caused-by-loyalty'))
        .map(v => v.voucherId),
    )

    if (loyaltyVoucherIds.size === 0) return 0

    return promo.items.reduce((total, item) => {
      if (!item.appliedVoucher) return total
      return total + item.appliedVoucher
        .filter(av => loyaltyVoucherIds.has(av.voucherId))
        .reduce((sum, av) => sum + av.amount, 0)
    }, 0)
  },
)

export const getCheckoutBenefitsView = createSelector(
  (state: App.State) => getAvailableAccommodationBenefits(state),
  (state: App.State) => getAvailableInsuranceBenefits(state),
  (state: App.State) => getAvailableBookingProtectionBenefits(state),
  (state: App.State) => getAvailableCruiseBenefits(state),
  (state: App.State) => getAvailableExperienceBenefits(state),
  (state: App.State) => state.luxPlus.products.subscription,
  (state: App.State) => getShouldShowLuxPlusUpsellForTour(state),
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => getVillaViews(state),
  (state: App.State) => getFlightTotals(state),
  (state: App.State) => getGiftCardItems(state),
  (state: App.State) => getLuxPlusLoyaltyVoucherSavings(state),
  (
    accommodationBenefits,
    insuranceBenefits,
    bookingProtectionBenefits,
    cruiseBenefits,
    experienceBenefits,
    productSubscription,
    showLuxPlusUpsellForTour,
    accommodationOfferView,
    villaViews,
    flightTotals,
    giftCardItems,
    loyaltyVoucherSavings,
  ): App.WithDataStatus<App.Checkout.LuxPlusCheckoutBenefitsView | undefined> => {
    const {
      membershipRequired,
    } = accommodationBenefits

    const allBenefitTypes = [accommodationBenefits, cruiseBenefits, insuranceBenefits, bookingProtectionBenefits, experienceBenefits]

    const offerViewsHaveRequiredData = accommodationOfferView.hasRequiredData && villaViews.hasRequiredData

    const {
      totalMemberPriceSavings,
      totalPrice: benefitsTotalPrice,
      totalMemberPrice: benefitsTotalMemberPrice,
      totalBaseMemberPrice,
    } = allBenefitTypes.reduce((acc, benefit) => ({
      totalMemberPriceSavings: acc.totalMemberPriceSavings + benefit.totalMemberPriceSavings,
      totalPrice: acc.totalPrice + benefit.totalPrice,
      totalMemberPrice: acc.totalMemberPrice + benefit.totalMemberPrice,
      totalBaseMemberPrice: acc.totalBaseMemberPrice + benefit.totalBaseMemberPrice,
    }), {
      totalMemberPriceSavings: 0,
      totalPrice: 0,
      totalMemberPrice: 0,
      totalBaseMemberPrice: 0,
    })

    const isBundledWithAccommodation = accommodationOfferView.data.some(view => view.isBundled)
    const hasStandaloneFlights = !isBundledWithAccommodation && flightTotals.data.price > 0
    const hasGiftCards = giftCardItems.length > 0

    let totalPrice = benefitsTotalPrice
    let totalMemberPrice = benefitsTotalMemberPrice

    if (hasStandaloneFlights) {
      totalPrice += flightTotals.data.price
      totalMemberPrice += flightTotals.data.price
    }

    if (hasGiftCards) {
      const giftCardTotal = sum(giftCardItems, item => item.amount)
      totalPrice += giftCardTotal
      totalMemberPrice += giftCardTotal
    }

    const inclusions = [...accommodationBenefits.inclusions, ...cruiseBenefits.inclusions]
    const hasRequiredData = offerViewsHaveRequiredData && !!productSubscription.offers?.length && !productSubscription.fetching && !insuranceBenefits.fetching && !bookingProtectionBenefits.fetching

    if (!!productSubscription.error ||
      (!membershipRequired &&
        !cruiseBenefits.hasLuxPlusInclusions &&
        !accommodationBenefits.hasLuxPlusInclusions &&
        totalBaseMemberPrice === 0 &&
        !showLuxPlusUpsellForTour &&
        !hasStandaloneFlights &&
        !hasGiftCards)
    ) {
      return {
        hasRequiredData: hasRequiredData || !!productSubscription.error,
        data: undefined,
      }
    }

    const firstAccommodationOffer = accommodationOfferView.data[0]?.offer as App.Offer | App.Tours.TourV2Offer | undefined

    return {
      data: {
        accommodation: accommodationBenefits,
        insurance: insuranceBenefits,
        bookingProtection: bookingProtectionBenefits,
        cruise: cruiseBenefits,
        combined: {
          totalMemberPriceSavings,
          totalPrice,
          totalBaseMemberPrice,
          totalMemberPrice,
          inclusions,
          loyaltyVoucherSavings,
        },
        durationLabel: accommodationOfferView.data.map(view => view.durationLabel).join(', '),
        offerTitle: accommodationOfferView.data.map(view => view.subLabel ?? view.mainLabel).join(', '),
        firstAccommodationOffer,
      },
      hasRequiredData,
    }
  },
)
